/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import axios from 'axios';
import { useFormik } from 'formik';
import { get, set, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, ButtonGroup, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { updateUser } from '../../../../actions/auth.actions';
import Button from '../../../../shared/components/Button';
import FormControl from '../../../../shared/components/Form/FormControl';
import Select from '../../../../shared/components/Select';
import useIsMounted from '../../../../shared/hooks/useIsMounted';
import moment from 'moment';
import OauthButton from '../../../../shared/components/OauthButton/OauthButton';
import userSession from '../../../../utils/userSession';
import Cropper from '../../../../shared/components/Cropper/Cropper';
import UserThumbnail from '../../../../shared/components/UserThumbnail/UserThumbnail';
import { uploadFile } from '../../../../utils/s3';
import PrimaryAddress from '../../utils/addresses/PrimaryAddress';

const GENERAL_INFORMATION_FIELDS = [
  'first_name',
  'last_name',
  'phone',
  'email'
];

const General = ({
  updateUser
}) => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [state, setState] = useState({
    loading: false,
    countries: [],
    user: {}
  });
  const [showAddressConfirm, setShowAddressConfirm] = useState(false);
  const userData = useSelector(store => get(store, 'auth.user'));
  const simulatedData = useSelector(store => get(store, 'config.simulatedData'));
  const simulatedUser = useSelector(store => get(store, 'config.simulatedUser'));
  const loadingSimulatedUser = useSelector(store => get(store, 'config.loadingSimulatedUser'));

  /* If is loading simulated users data wait for it and then decides to use user data or simulated */
  useEffect(() => {
    if (!loadingSimulatedUser) {
      const user = simulatedData || userData;
      setState({
        ...state,
        user,
        profilePic: user.best_pic || null
      });
      formik.setValues(initFormValues(user));
    }
  }, [loadingSimulatedUser]);

  /* If the simulated user is not longer active restore data to original user */
  useEffect(() => {
    if (!loadingSimulatedUser && !simulatedUser) {
      setState({
        ...state,
        user: userData,
        profilePic: userData.best_pic || null
      });
      formik.setValues(initFormValues(userData));
    }
  }, [simulatedUser]);

  const isAdmin = userSession('admin') && !simulatedUser;

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .required(`${t('influencers.pages.profile.general.first_name')} ${t('influencers.common.is_required')}`),
    last_name: Yup.string()
      .required(`${t('influencers.pages.profile.general.last_name')} ${t('influencers.common.is_required')}`),
    phone: Yup.string()
      .required(`${t('influencers.pages.profile.general.phone')} ${t('influencers.common.is_required')}`),
    email: Yup.string()
      .required(`${t('influencers.pages.profile.general.email')} ${t('influencers.common.is_required')}`),
    dob: Yup.date(),
    paypal_email: Yup.string()
      .required(`${t('influencers.pages.profile.general.paypal_email')} ${t('influencers.common.is_required')}`),
    lumanu_email: Yup.string().email(),
    address: Yup.object({
      first_name: Yup.string()
        .required(`${t('influencers.pages.profile.general.first_name')} ${t('influencers.common.is_required')}`),
      last_name: Yup.string()
        .required(`${t('influencers.pages.profile.general.last_name')} ${t('influencers.common.is_required')}`),
      address: Yup.string()
        .required(`${t('influencers.pages.profile.general.address')} ${t('influencers.common.is_required')}`),
      address_2: Yup.string(),
      city: Yup.string()
        .required(`${t('influencers.pages.profile.general.city')} ${t('influencers.common.is_required')}`),
      state: Yup.string()
        .required(`${t('influencers.pages.profile.general.state')} ${t('influencers.common.is_required')}`),
      zip: Yup.string()
        .required(`${t('influencers.pages.profile.general.zip')} ${t('influencers.common.is_required')}`),
      country: Yup.string()
        .required(`${t('influencers.pages.profile.general.country')} ${t('influencers.common.is_required')}`),
      id: Yup.string().nullable()
    }),
    best_pic: Yup.string()
  });

  useEffect(() => {
    axios.get('/api/v1/countries')
      .then(({ data }) => {
        setState(prev => ({
          ...prev,
          countries: data.countries || []
        }));
      });
  }, []);

  const { user } = state;

  const initFormValues = useCallback((user = {}) => ({
    first_name: get(user, 'first_name') || '',
    last_name: get(user, 'last_name') || '',
    email: get(user, 'email') || '',
    phone: get(user, 'phone') || '',
    dob: get(user, 'dob') || '',
    paypal_email: get(user, 'paypal_email') || '',
    lumanu_email: get(user, 'lumanu_email') || '',
    address: {
      id: get(user, 'address.id'),
      first_name: get(user, 'address.first_name') || '',
      last_name: get(user, 'address.last_name') || '',
      address: get(user, 'address.address') || '',
      address_2: get(user, 'address.address_2') || '',
      city: get(user, 'address.city') || '',
      state: get(user, 'address.state') || '',
      zip: get(user, 'address.zip') || '',
      phone: get(user, 'address.phone') || '',
      country: get(user, 'address.country') || '',
    },
    user_pic: get(user, 'best_pic') || null
  }), []);

  const formik = useFormik({
    initialValues: initFormValues(),
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { setStatus }) => {
      setStatus({});
      setState({
        ...state,
        loading: true
      });
      axios.patch(`/api/v1/influencers/users/${user.id}`, {
        user: setUserParamValues(values)
      }).then(({ data }) => {
        updateUser(data.user);
        if (data.show_address_confirm) {
          formik.setValues({ ...initFormValues(data.user), address: formik.values.address });
          setState({ ...state, errors: {}, loading: false });
          setShowAddressConfirm(true);
        } else {
          formik.setValues(initFormValues(data.user));
          setState({ ...state, errors: {}, loading: false });
        }
      }).catch((e) => {
        if (isMounted) {
          const errors = Object.assign(get(e.response, 'data.errors', {}));

          const mappedFields = [
            'first_name', 'last_name', 'email', 'phone', 'dob', 'paypal_email', 'lumanu_email'
          ];
          const humanize = (key) => key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
          const flatMsg = (msgs) => [].concat(msgs).flat().join(', ');
          const generalErrors = Object.entries(errors)
            .filter(([key]) =>
              !mappedFields.includes(key) &&
              !key.startsWith('addresses.') &&
              key !== 'current_permanent_address'
            )
            .map(([key, msgs]) => `${humanize(key)} ${flatMsg(msgs)}`);

          setStatus({
            first_name: errors.first_name,
            last_name: errors.last_name,
            email: errors.email,
            phone: errors.phone,
            dob: errors.dob,
            paypal_email: errors.paypal_email,
            lumanu_email: errors.lumanu_email,
            general: generalErrors.length ? generalErrors : null,
            address: {
              first_name: errors['addresses.first_name'],
              last_name: errors['addresses.last_name'],
              address: errors['addresses.address'],
              city: errors['addresses.city'],
              state: errors['addresses.state'],
              zip: errors['addresses.zip'],
              country: errors['addresses.country'],
              general: get(errors, 'current_permanent_address') || get(errors, 'addresses.address.easypost')
            }
          });

          setState({ ...state, loading: false });
        }
      });
    }
  });



  const renderInfoFields = useCallback(() => {
    const generalFields = GENERAL_INFORMATION_FIELDS.map((field, idx) => {
      const isTouched = get(formik, `touched.${field}`, false);
      const hasError = get(formik, `errors.${field}`, false) || get(formik.status, `${field}`, false);
      const lastElement = (idx + 1) % 2 === 0;

      return (
        <React.Fragment key={field}>
          <Col xs={12} sm={4}>
            <Form.Group>
              <Form.Label>{t(`influencers.pages.profile.general.${field}`)}</Form.Label>
              <FormControl
                type="text"
                placeholder={t("influencers.common.text")}
                name={field}
                onChange={formik.handleChange}
                value={formik.values[field]}
                error={isTouched && hasError}
                disabled={state.loading}
                onBlur={formik.handleBlur}
              />
            </Form.Group>
            {isTouched && hasError &&
              <Form.Text className="text-muted danger-text">
                {hasError}
              </Form.Text>
            }
          </Col>
          {lastElement && <Col sm={4} />}
        </React.Fragment>
      );
    });

    const salesforce_oauth_token = userSession('salesforce_oauth_token');

    return (
      <React.Fragment>
        {generalFields}

        <Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>
              Birthdate
            </Form.Label>
            <FormControl
              type='date'
              max={moment(new Date()).format('YYYY-MM-DD')}
              name="dob"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.dob}
              error={(get(formik, 'touched.dob') && get(formik, 'errors.dob')) || !!get(formik.status, 'dob')}
              disabled={state.loading}
            />
            {((get(formik, 'touched.dob') && get(formik, 'errors.dob')) || get(formik.status, 'dob')) ?
              <Form.Text className="text-muted danger-text">
                {get(formik, 'errors.dob') || get(formik.status, 'dob')}
              </Form.Text> : null}
          </Form.Group>
        </Col>


        {isAdmin ?
          <Col xs={12} md={12}>
            <h6>
              {salesforce_oauth_token ? t(`influencers.pages.profile.general.salesforce_connected`) : t(`influencers.pages.profile.general.salesforce_not_connected`)}
            </h6>
            <OauthButton platform="salesforce" />
          </Col>
          : null}

      </React.Fragment>
    );
  }, [formik, state]);

  const handleDiscardChanges = useCallback(() => {
    document.querySelector('body').scrollTo({ top: 0, behavior: 'smooth' });
    formik.resetForm();
  }, [formik]);

  return (
    <Container className="page" as={Form} onSubmit={formik.handleSubmit} >
      <h3 className='mb-4'>{t('influencers.pages.profile.general.title')}</h3>
      {!isEmpty(get(formik.status, 'general')) &&
        <Alert variant="danger" className="mb-4">
          {get(formik.status, 'general').map((msg, i) => <div key={i}>{msg}</div>)}
        </Alert>
      }
      {/* Profile Picture */}
      {isAdmin && <Row>
        <Col xs={12} md={6} className="d-flex gap-16 align-items-end mb-3">
          <UserThumbnail
            imageUrl={formik.values.user_pic}
            size='xll'
            initials={user.first_name} />
          <Form.Group>
            <Form.Label> Profile Pic </Form.Label>
            <Cropper shape='circular'
              onChange={(file) => {
                const formData = new FormData();
                formData.append("file", new Blob([file], { type: file.type }), file.name);
                formData.set("filetype", file.type);

                uploadFile(file, (progressEvent) => {
                  const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                  setState(state => ({
                    ...state,
                    uploading: true,
                    progress: percentCompleted,
                  }));
                }, { checkAvifOnly: false }).then((response) => {
                  if (get(response, 'data.success') === false) {
                    setState({
                      progress: 100,
                      uploading: false,
                      error: true,
                      fileUrl: null
                    });
                    return;
                  }
                  formik.setFieldValue('user_pic', response.data.url);
                  setState(state => ({
                    ...state,
                    uploading: false,
                    success: true,
                    fileUrl: response.data.url,
                  }));
                }).catch((e) => {
                  setState({
                    ...state,
                    progress: 0,
                    uploading: false,
                    error: true,
                    fileUrl: null
                  });
                });
              }} />
          </Form.Group>
        </Col>
      </Row>}

      <Row>
        {renderInfoFields()}
      </Row>
      <hr className='my-6' />
      <h3 className='mb-4'>{t('influencers.pages.profile.general.primary_address')}</h3>
      <Row>
        <PrimaryAddress
          formik={formik}
          countries={state.countries}
          loading={state.loading}
        />
      </Row>
      {!isEmpty(get(formik.status, 'address.general')) &&
        <Form.Text className="text-muted danger-text">
          {get(formik.status, 'address.general')}
        </Form.Text>
      }
      <hr className='my-6' />
      <h3 className='mb-4'>{t('influencers.pages.profile.general.payment')}</h3>
      <Row>
        <Col xs={12} sm={4}>
          <Form.Group>
            <Form.Label>{t('influencers.pages.profile.general.paypal_email')}</Form.Label>
            <FormControl type="email"
              placeholder={t("influencers.common.text")}
              name={'paypal_email'}
              onChange={formik.handleChange}
              value={formik.values.paypal_email}
              disabled={state.loading}
              error={!!get(formik.status, 'paypal_email')}
            />
            {get(formik.status, 'paypal_email') &&
              <Form.Text className="text-muted danger-text">
                {get(formik.status, 'paypal_email')}
              </Form.Text>
            }
          </Form.Group>
        </Col>
        <Col xs={12} sm={4}>
          <Form.Group>
            <Form.Label>{t('influencers.pages.profile.general.lumanu_email')}</Form.Label>
            <FormControl type="email"
              placeholder={t("influencers.common.text")}
              name={'lumanu_email'}
              onChange={formik.handleChange}
              value={formik.values.lumanu_email}
              disabled={state.loading}
              error={!!get(formik.status, 'lumanu_email')}
            />
            {get(formik.status, 'lumanu_email') &&
              <Form.Text className="text-muted danger-text">
                {get(formik.status, 'lumanu_email')}
              </Form.Text>
            }
          </Form.Group>
        </Col>
      </Row>
      <Modal show={showAddressConfirm} onHide={() => setShowAddressConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('influencers.pages.campaign.apply_side_pane.confirm_shipping_address')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('influencers.pages.campaign.apply_side_pane.confirm_address_message')}</p>
          <div className="p-3 bg-light rounded text-1">
            <div>{get(formik.values, 'address.first_name')} {get(formik.values, 'address.last_name')}</div>
            <div>{get(formik.values, 'address.address')}</div>
            {get(formik.values, 'address.address_2') && <div>{get(formik.values, 'address.address_2')}</div>}
            <div>{get(formik.values, 'address.city')}, {get(formik.values, 'address.state')} {get(formik.values, 'address.zip')}</div>
            <div>{get(formik.values, 'address.country')}</div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={() => setShowAddressConfirm(false)}>
            {t('influencers.pages.campaign.apply_side_pane.edit_address')}
          </Button>
          <Button variant="primary" type="button" onClick={() => {
            setState({ ...state, loading: true });
            axios.patch(`/api/v1/influencers/users/${user.id}`, {
              user: { ...setUserParamValues(formik.values), confirm_address: true }
            }).then(({ data }) => {
              updateUser(data.user);
              formik.setFieldValue('address.id', get(data, 'user.address.id') || get(formik.values, 'address.id'));
              setState({ ...state, errors: {}, loading: false });
              setShowAddressConfirm(false);
            }).catch(() => {
              setState({ ...state, loading: false });
              setShowAddressConfirm(false);
            });
          }}>
            {t('influencers.pages.campaign.apply_side_pane.address_looks_correct')}
          </Button>
        </Modal.Footer>
      </Modal>

      <hr className='my-6' />
      <Row className='justify-content-end'>
        <div className='col-auto'>
          <ButtonGroup>
            <Button
              variant="secondary"
              type="button"
              onClick={handleDiscardChanges}
              disabled={state.loading}>
              {t('influencers.pages.profile.general.discard_changes')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={state.loading || !formik.isValid}>
              {state.loading
                ? <Spinner animation="border" size="sm" />
                : t('influencers.pages.profile.general.save_changes')
              }
            </Button>
          </ButtonGroup>
        </div>
      </Row>
    </Container>
  );
};
const mapStateToProps = (state) => ({
});

const mapDispatchToProps = (dispatch) => ({
  updateUser: (user) => dispatch(updateUser(user)),
});

// Set the address nested object needed for the rails signature
const setUserParamValues = (userValues) => {
  let userParams = JSON.parse(JSON.stringify(userValues));
  userParams.address.phone = userParams.phone;
  userParams.addresses_attributes = [userParams.address];
  delete userParams.address;
  return userParams;
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(General));
